import { useEffect, useState } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';

import { generateKioskQr, sendKioskQrToEmail } from 'api/Kiosk';

import { useKiosks } from 'lib/hooks/queries/kiosks/useKiosks';
import { useCustomMutation } from 'lib/hooks/useCustomMutation';
import useModal from 'lib/hooks/useModal';

import KioskModal from 'components/practice/Settings/components/Kiosks/KioskModal';
import Button from 'components/shared/Button/Button';
import Icon from 'components/shared/Icon/Icon';
import Popover from 'components/shared/Popovers/Popover/Popover';

import SubstepConfirmationModal from '../SubstepConfirmationModal/SubstepConfirmationModal';

import KioskCard from './KioskCard';
import CustomEmailModal from './components/CustomEmailModal';

const WizardKiosks = ({
  updateConfigs,
  setHeaderButtonProps,
  setFooterConfig,
  onNext,
  onPrev,
  mutateOrchestratorData,
  orchestratorLoading
}) => {
  const { isOpen, openModal, closeModal } = useModal(false);
  const {
    isOpen: isEmailModalOpen,
    openModal: openEmailModal,
    closeModal: closeEmailModal
  } = useModal(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const { practice } = useOutletContext();
  const navigate = useNavigate();

  const { data } = useKiosks({
    params: { practiceId: practice?.id },
    dependencies: [practice?.id]
  });

  const handleDownloadQr = (qrDataUrl) => {
    // Convert data URL to blob
    const arr = qrDataUrl.split(',');
    const mime = arr[0].match(/:(.*?);/)[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    const blob = new Blob([u8arr], { type: mime });

    // Create download link and trigger download
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `kiosk-qr-${practice?.id}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(link.href);
  };

  const { mutate: generateQr } = useCustomMutation({
    mutationFn: () => generateKioskQr(navigate, { practiceId: practice?.id }),
    onSuccess: (data) => {
      if (data?.qr_code_image) {
        handleDownloadQr(data.qr_code_image);
      }
    },
    disableAlert: true
  });

  const { mutate: sendQrToEmail } = useCustomMutation({
    mutationFn: () =>
      sendKioskQrToEmail(navigate, { practiceId: practice?.id, email: practice?.email }),
    successMessage: 'Kiosk QR code sent to your email successfully.'
  });

  useEffect(() => {
    setHeaderButtonProps({ text: 'Add kiosk', onClick: openModal });
    updateConfigs({
      message: 'Please register all kiosks used in your practice',
      goBackHandler: onPrev,
      aboveFooterContent: (
        <div className="my-3 flex w-full items-center justify-between rounded-2xl bg-primary-25 px-6 py-2">
          <div className="flex items-center gap-2.5">
            <div className="rounded-lg border border-solid border-neutral-200 bg-white p-2">
              <Icon size={32} icon="kiosk-mono" color="primary" />
            </div>
            <div className="flex flex-col gap-0.5">
              <p className="font-600">Patient check-in access</p>
              <p className="text-sm">Use the QR code to enable patient self check-in.</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button
              iconShade={900}
              color="neutral"
              variant="tertiary"
              text="Download"
              onClick={generateQr}
              size="small"
              className="rounded-md"
              icon="import-mono"
            />
            <Popover
              options={[
                {
                  label: practice?.email,
                  onClick: sendQrToEmail
                },
                {
                  label: 'Custom email address',
                  onClick: openEmailModal
                }
              ]}
              isFixed
              menuPortalTarget={document.body}
              position="right"
              text="Email"
              icon="send-mono"
              buttonTheme="tertiary"
              buttonClassName="bg-white hover:bg-neutral-100 border border-solid border-neutral-200 h-10 rounded"
              iconRight="chevron-down-2-linear"
            />
            <Icon icon="qr-code-placeholder" />
          </div>
        </div>
      ),
      actions: [
        {
          text: 'Continue',
          onClick: () => {
            if (!data?.kiosks?.length) {
              setShowConfirmModal(true);
              return;
            }
            mutateOrchestratorData({
              kioskIds: (data?.kiosks ?? []).map((k) => k?.id)
            });
          },
          disabled: orchestratorLoading
        }
      ]
    });
  }, [data, orchestratorLoading, practice?.email, generateQr, sendQrToEmail]);

  useEffect(() => {
    return () => {
      setFooterConfig((prev) => ({ ...prev, aboveFooterContent: null }));
    };
  }, []);

  return (
    <>
      <div className="flex h-full w-full flex-col">
        <div className="min-h-0 flex-1 overflow-y-auto">
          {!data?.kiosks?.length && (
            <div className="flex w-full flex-col items-center gap-2 py-6">
              <Icon icon="no-kiosk" />
              <p className="text-base font-600 text-neutral-900">No kiosk added yet</p>
              <p className="text-sm text-neutral-600">
                Add services to define your practice's schedule of fees
              </p>
              <Button onClick={openModal} text="Add kiosk" variant="link" icon="add-mono" />
            </div>
          )}
          {data?.kiosks?.length > 0 && (
            <div className="grid grid-cols-2 gap-5 pb-5">
              {data?.kiosks?.map((kiosk) => (
                <KioskCard key={kiosk.id} kiosk={kiosk} />
              ))}
            </div>
          )}
        </div>
      </div>
      <KioskModal isOpen={isOpen} handleClose={closeModal} tableData={data?.kiosks} />
      <CustomEmailModal open={isEmailModalOpen} handleClose={closeEmailModal} />
      <SubstepConfirmationModal
        isOpen={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        onMarkComplete={() => {
          setShowConfirmModal(false);
          mutateOrchestratorData({ kioskIds: [], skipKioskSetup: true });
        }}
        onSkip={() => {
          setShowConfirmModal(false);
          onNext();
        }}
        primaryActionText="Add Kiosk"
        onPrimaryAction={() => {
          setShowConfirmModal(false);
          openModal();
        }}
        title="No kiosks registered"
        message="You haven't registered any kiosks yet. Choose how you'd like to proceed before moving on."
        isLoading={orchestratorLoading}
      />
    </>
  );
};

export default WizardKiosks;
